/**
 * NoteActionButtonsContainer - Container for note action buttons
 *
 * Manages callback handlers for share, edit, and delete actions on a specific note.
 * Wraps the noteId and theme with appropriate memoized callbacks.
 * @remarks This is a Container component - handles all logic and state management
 */

import { useCallback, memo } from "react";

import NoteActionButtonsView from "./NoteActionButtonsView";

/**
 * Props for NoteActionButtonsContainer component
 */
interface NoteActionButtonsContainerProps {
  /** Note ID */
  readonly noteId: string;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Number of users this note has been shared with */
  readonly shareCount: number;
  /** Handler for sharing note */
  readonly onShareNote: (noteId: string) => void;
  /** Handler for editing note */
  readonly onEditNote: (noteId: string) => void;
  /** Handler for deleting note */
  readonly onDeleteNote: (noteId: string) => void;
  /** Whether to show the share button (hidden when org defaults to public) */
  readonly showShareButton?: boolean;
}

/**
 * NoteActionButtonsContainer manages action button callbacks
 * @param props - Container props
 * @param props.noteId - Note ID
 * @param props.isDark - Whether dark mode is active
 * @param props.shareCount - Number of users this note has been shared with
 * @param props.onShareNote - Handler for sharing note
 * @param props.onEditNote - Handler for editing note
 * @param props.onDeleteNote - Handler for deleting note
 * @param props.showShareButton - Whether to show the share button
 * @returns NoteActionButtonsView component
 */
const NoteActionButtonsContainer = ({
  noteId,
  isDark,
  shareCount,
  onShareNote,
  onEditNote,
  onDeleteNote,
  showShareButton,
}: NoteActionButtonsContainerProps) => {
  const handleShare = useCallback(() => {
    onShareNote(noteId);
  }, [noteId, onShareNote]);

  const handleEdit = useCallback(() => {
    onEditNote(noteId);
  }, [noteId, onEditNote]);

  const handleDelete = useCallback(() => {
    onDeleteNote(noteId);
  }, [noteId, onDeleteNote]);

  return (
    <NoteActionButtonsView
      isDark={isDark}
      shareCount={shareCount}
      onSharePress={handleShare}
      onEditPress={handleEdit}
      onDeletePress={handleDelete}
      showShareButton={showShareButton}
    />
  );
};

NoteActionButtonsContainer.displayName = "NoteActionButtonsContainer";

export default memo(NoteActionButtonsContainer);
